import axios from 'axios';
import config, { getApiKey } from './environment';
import { forceLogout } from '../utils/apiKeyAuth';
import { detectLockout, signalLockout } from '../utils/lockout';

/**
 * Create and configure axios instance with default settings
 * This configuration is shared across all API services
 */
const createAxiosInstance = (customConfig = {}) => {
  const axiosInstance = axios.create({
    baseURL: config.API_BASE_URL,
    timeout: 30000, // 30 seconds timeout
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'x-api-key': getApiKey(),
      ...customConfig.headers
    },
    ...customConfig
  });

  // Request interceptor
  axiosInstance.interceptors.request.use(
    (requestConfig) => {
      // Ensure x-api-key is always present (get from localStorage dynamically)
      if (!requestConfig.headers['x-api-key']) {
        requestConfig.headers['x-api-key'] = getApiKey();
      }
      
      // Add timestamp to prevent caching
      requestConfig.params = {
        ...requestConfig.params,
        _t: Date.now()
      };
      
      // Log request in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`🚀 API Request: ${requestConfig.method?.toUpperCase()} ${requestConfig.url}`, {
          data: requestConfig.data instanceof FormData ? 'FormData' : requestConfig.data,
          params: requestConfig.params,
          headers: requestConfig.headers,
          hasApiKey: !!requestConfig.headers['x-api-key']
        });
      }
      
      return requestConfig;
    },
    (error) => {
      console.error('❌ Request Error:', error);
      return Promise.reject(error);
    }
  );

  // Response interceptor
  axiosInstance.interceptors.response.use(
    (response) => {
      // Log response in development
      if (process.env.NODE_ENV === 'development') {
        console.log(`✅ API Response: ${response.config.method?.toUpperCase()} ${response.config.url}`, {
          status: response.status,
          data: response.data
        });
      }
      
      return response;
    },
    (error) => {
      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const { status, data } = error.response;
        console.error(`❌ API Error ${status}:`, data);

        // ── Global auth handling (spec §2.1) ──────────────────────────────────
        // Centralised here so every x-api-key call (default + form-data clients)
        // inherits it. NOTE: the backend returns these messages under `error` (not
        // detail/message) — detectLockout reads it first, otherwise "…deactivated"
        // isn't matched and the 403 falls through to the re-fetch path below,
        // looping forever.
        const reqUrl = error.config?.url || '';
        const isPermissionsBootstrap = /\/user\/me\/permissions/.test(reqUrl);
        const lockoutKind = detectLockout(status, data);
        if (status === 401) {
          // Invalid / unknown / expired key — used to surface as a 500.
          forceLogout('Your session has expired or your API key is invalid. Please sign in again.');
        } else if (lockoutKind) {
          // Partner / company / user deactivated (Partner tier guide §9): swap to
          // the full-page lockout screen but keep the session, so access comes
          // back via "Try again" once reactivated.
          signalLockout(lockoutKind);
        } else if (status === 403 && !isPermissionsBootstrap) {
          // Missing-permission 403 on a feature call: routes/menus normally prevent
          // this, but if it slips through, nudge the permission cache to reconcile.
          // Never fire this for the permissions bootstrap call itself — refetching
          // permissions because the permissions call 403'd is an infinite loop.
          try {
            window.dispatchEvent(new Event('rbac:refresh'));
          } catch (e) {
            /* no-op */
          }
        }

        // Transform error for consistent handling
        const errorMessage = data?.message || data?.detail || data?.error || `HTTP Error ${status}`;
        const transformedError = new Error(errorMessage);
        transformedError.status = status;
        transformedError.data = data;
        
        return Promise.reject(transformedError);
      } else if (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '')) {
        // Axios timeout (client-side abort)
        console.error('⏱️ Request Timeout:', error.message);
        const timeoutError = new Error('Request timed out. The server may still be processing this task.');
        timeoutError.isTimeout = true;
        timeoutError.code = error.code;
        return Promise.reject(timeoutError);
      } else if (error.code === 'ERR_CANCELED') {
        // Request canceled by browser/navigation/abort controller
        console.error('🛑 Request Cancelled:', error.message);
        const canceledError = new Error('Request was cancelled before completion.');
        canceledError.isCanceled = true;
        canceledError.code = error.code;
        return Promise.reject(canceledError);
      } else if (error.request) {
        // Request was made but no response received
        console.error('❌ Network Error:', error.message);
        const networkError = new Error('Network Error: Please check your connection');
        networkError.isNetworkError = true;
        return Promise.reject(networkError);
      } else {
        // Something else happened
        console.error('❌ Request Setup Error:', error.message);
        return Promise.reject(error);
      }
    }
  );

  return axiosInstance;
};

// Default axios instance
const defaultAxiosInstance = createAxiosInstance();

// Specialized instance for multipart/form-data requests
const formDataAxiosInstance = createAxiosInstance();

// Add special interceptor for FormData requests to remove Content-Type
// and let axios set it automatically with proper boundary
formDataAxiosInstance.interceptors.request.use(
  (requestConfig) => {
    // For FormData, remove the Content-Type header to let axios handle it
    if (requestConfig.data instanceof FormData) {
      delete requestConfig.headers['Content-Type'];
    }
    return requestConfig;
  },
  (error) => Promise.reject(error)
);

// Export factory function and pre-configured instances
export { createAxiosInstance, defaultAxiosInstance, formDataAxiosInstance };
export default defaultAxiosInstance;
